import { useEffect, useState } from "react"

import { Checkbox, DatePicker, Flex, Skeleton, Text } from "@/atoms"
import { getDate, MONTH_YEAR } from "@/helpers/date.helpers"
import { useNumberConvertor } from "@/hooks/useNumberConvertor"
import { useRuntimeCoverage } from "@/hooks/useRuntimeCoverage"
import { useTranslation } from "@/hooks/useTranslation"
import { useFeatureFlags } from "@/hooks/useUser"
import { ExploitIcon, Fix, Runtime } from "@/icons"
import { QuickFilterDropdown } from "@/molecules/QuickFilterDropdown"
import { SeverityTag } from "@/molecules/SeverityTag"
import { getDefaultSinceDate } from "@/pages/DashboardPage/applications/useStatistics"

import styles from "./FilterTitleCard.module.scss"

import type { CheckboxChangeEvent } from "@/atoms"
import type { Severity } from "@/domain/vulnerability"
import type { FilterTitleCardProps } from "."

export const FilterTitleCard = (props: FilterTitleCardProps) => {
  const { t } = useTranslation()
  const countFilters = (filters: Record<string, unknown>) =>
    Object.keys(filters || {}).filter((key) => !!filters[key]).length
  const [{ count, dirty, filters }, setState] = useState<{
    count: number
    dirty: boolean
    filters: Record<string, any>
  }>({ dirty: false, count: countFilters(props.values), filters: props.values })
  const { isLowRuntimeCoverage } = useRuntimeCoverage()

  const onChange = (name: string) => (e: CheckboxChangeEvent) =>
    setState(({ dirty, filters, ...prev }) => ({
      dirty: true,
      filters: { ...filters, [name]: !!e.target.checked },
      ...prev
    }))

  const { abbreviateNumber } = useNumberConvertor()
  const { loading, severity } = props
  const kortexInstalledFF = useFeatureFlags("kortexInstalled")
  const runtimeDisabled = !kortexInstalledFF
  const severities = [
    "critical",
    "high",
    "medium",
    "low"
  ] as const satisfies Severity[]
  const severityKey = "severity"

  const disabled =
    kortexInstalledFF === undefined || isLowRuntimeCoverage === undefined

  useEffect(() => {
    if (disabled) return

    if (isLowRuntimeCoverage || kortexInstalledFF === false) {
      const nextFilters = { ...filters, runtime: false }
      const count = countFilters(nextFilters)

      props.onOk?.(nextFilters)
      setState(({ filters: _, count: __, ...prev }) => {
        return {
          count,
          filters: nextFilters,
          ...prev
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled])

  return (
    <Flex gap={6} align="center" className={styles.container}>
      {loading && <Skeleton.Input className={styles.skeleton} size="small" />}
      {!loading && (
        <Text className={styles.filteredText}>
          {t("dashboard.filtered", {
            value: abbreviateNumber(props.count || 0),
            total: abbreviateNumber(props.total || 0)
          })}
        </Text>
      )}
      <QuickFilterDropdown
        disabled={disabled}
        count={count}
        items={[
          {
            key: "date",
            label: t("dashboard.filters.issuesSince"),
            type: "group" as const,
            className: styles.group,
            children: [
              {
                key: "since",
                className: styles.since,
                label: (
                  <DatePicker
                    picker="month"
                    format={MONTH_YEAR}
                    minDate={getDefaultSinceDate()}
                    maxDate={getDate()}
                    allowClear={false}
                    defaultValue={props.values?.since}
                    onChange={(since) =>
                      setState(({ dirty, filters, ...prevState }) => ({
                        dirty: true,
                        filters: { ...filters, since },
                        ...prevState
                      }))
                    }
                  />
                )
              }
            ]
          },
          {
            key: "insight",
            label: t("general.insight"),
            type: "group" as const,
            className: styles.group,
            children: [
              {
                key: "runtime",
                label: (
                  <Checkbox
                    disabled={runtimeDisabled}
                    className={styles.checkbox}
                    checked={runtimeDisabled ? false : filters.runtime}
                    onChange={onChange("runtime")}
                  >
                    <Runtime className={styles.labelIcon} />
                    <span className={styles.labelText}>
                      {t("insights.runtime")}
                    </span>
                  </Checkbox>
                )
              },
              {
                key: "fixable",
                label: (
                  <Checkbox
                    className={styles.checkbox}
                    checked={filters.fixable}
                    onChange={onChange("fixable")}
                  >
                    <Fix className={styles.labelIcon} />
                    <span className={styles.labelText}>
                      {t("insights.fixable")}
                    </span>
                  </Checkbox>
                )
              },
              {
                key: "exploitMaturity",
                label: (
                  <Checkbox
                    className={styles.checkbox}
                    checked={filters.exploitMaturity}
                    onChange={onChange("exploitMaturity")}
                  >
                    <ExploitIcon
                      className={styles.labelIcon}
                      type="IN_THE_WILD"
                    />
                    <span className={styles.labelText}>
                      {t("insights.exploitable")}
                    </span>
                  </Checkbox>
                )
              }
            ]
          },
          {
            key: severityKey,
            label: t("general.severity"),
            type: "group" as const,
            className: styles.group,
            children: severities.map((severity) => {
              return {
                key: severity,
                label: (
                  <Checkbox
                    className={styles.checkbox}
                    checked={filters[severity]}
                    onChange={onChange(severity)}
                  >
                    <SeverityTag latter severity={severity} t={t} />
                    <span className={styles.labelText}>
                      {t(`vulnerabilities.${severity}`)}
                    </span>
                  </Checkbox>
                )
              }
            })
          }
        ].filter((item) => {
          if (!severity && item.key === severityKey) return false

          return true
        })}
        okProps={{
          disabled: !dirty,
          onClick: () => {
            props.onOk?.(filters)
            setState((prev) => ({
              ...prev,
              dirty: false,
              count: countFilters(filters)
            }))
          }
        }}
      />
    </Flex>
  )
}
